import { z } from 'zod'
import { oc } from '@orpc/contract'

/**
 * Binds a beHuman practitioner to a Zocdoc provider — the practitioner counterpart of writing
 * `ZOCDOC_PATIENT_SYSTEM` onto a chart.
 *
 * **One write.** `Practitioner.identifier` gains `ZOCDOC_PROVIDER_SYSTEM|pr_…`, and that is the
 * whole link — it is to the *person*, so it covers every location they are schedulable at. There
 * used to be a second half fanning the practitioner out over `zocdoc_provider_link` rows; that
 * table is gone, and with it the half-applied state a failure between the two could leave.
 *
 * A different value on either side is a **conflict, never an overwrite**: overwriting would
 * re-point one doctor's calendar at another person. Both directions are checked, which the
 * patient link does not have to do — there the identifier sits on the resource being pointed at,
 * while here a second practitioner could already claim this provider.
 */
export const zocdocLinkPractitioner = oc
  .route({ method: 'POST', path: '/v2/zocdoc/provider-links/link-practitioner' })
  .input(
    z.object({
      /**
       * Zocdoc's `provider_id` (`pr_…`), not a `pr_…|lo_…` entity id: the link is to the person,
       * and it applies to every location that person is schedulable at.
       */
      zocdocProviderId: z.string().min(1),
      /** FHIR `Practitioner` id — an id, not a reference. */
      practitionerId: z.string().min(1),
    }),
  )
  .output(
    z.object({
      status: z.enum([
        /** The identifier was written. */
        'linked',
        /** The practitioner already carried exactly this provider — nothing was written. */
        'already-linked',
        /** Refused: see `conflict`. Nothing was written on either side. */
        'conflict',
        /** Refused: see `refusal`. The link could never publish, so it is not written. */
        'refused',
      ]),
      /**
       * The link was verified against Zocdoc before being written, and failed
       * (see docs/ZOCDOC_AVAILABILITY_SYNC_PLAN.md).
       *
       * Both reasons are **persistent** — they would fail identically on every later publish, and
       * publishing is asynchronous, so nobody would find out. Refusing here puts the failure in
       * front of the one person who is present and can fix it.
       */
      refusal: z
        .object({
          reason: z.enum([
            /**
             * **Nobody** knows this practitioner's NPI — not the chart, and not Zocdoc's roster.
             * Only then is it a refusal: when the chart has none but Zocdoc reports one, the link
             * writes it onto the practitioner instead, since it is the same national identifier
             * and asserting it is what linking means.
             */
            'practitioner-has-no-npi',
            /**
             * Zocdoc lists no *schedulable* entity for that provider id. Usually a mistyped or
             * stale `pr_…`. Checked against the roster rather than `GET /v1/providers` because
             * being schedulable is the stronger condition: a provider Zocdoc knows but does not
             * list is one nothing could ever be published for.
             */
            'zocdoc-unknown',
          ]),
          detail: z.string(),
        })
        .optional(),
      conflict: z
        .object({
          kind: z.enum([
            /** This practitioner is already bound to a different Zocdoc provider. */
            'practitioner-has-other-provider',
            /** This Zocdoc provider is already bound to a different practitioner. */
            'provider-has-other-practitioner',
          ]),
          /** The value already in place — the other provider id, or the other practitioner id. */
          existing: z.string(),
        })
        .optional(),
    }),
  )
